import { useDroppable } from '@dnd-kit/react'
import type { GroupingGroup } from '@vedalib/editor/lib/elements'
import { FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import cn from 'classnames'
import * as R from 'ramda'
import { useContext } from 'use-context-selector'

import GroupingContext from 'components/Editor/EditorElements/elements/Grouping/GroupingContext'
import { getObjectFitDivMap } from 'components/Editor/EditorElements/utils/getObjectFitDivMap'
import { t } from 'services/Translation'
import { useUrlByUsage } from 'utils/files'

import ScaledAnswer from '../ScaledAnswer/ScaledAnswer'
import * as s from './DestinationCard.module.scss'

interface IDestinationProps {
  group: GroupingGroup
}

const DestinationCard = ({ group }: IDestinationProps) => {
  const { styles, hasGroupImage, testResult, font } = useContext(GroupingContext)
  const { ref, isDropTarget } = useDroppable({ id: group.value, data: group })
  const imageUrl = useUrlByUsage(group.target.image, FileUsageTypeEnum.image)
  const labelClasses = cn(s.label, { [s.placeholder]: !group.target.label })
  const label =
    group.target.label || (group?.target.image?.id && hasGroupImage)
      ? group.target.label || ''
      : t('elements.grouping.placeholder.group')

  return (
    <div
      aria-label={
        group.target.label ||
        group.target.image?.accessibility ||
        t('elements.grouping.placeholder.group')
      }
      className={s.destItem}
      ref={ref as React.RefCallback<HTMLDivElement>}
      role='listitem'
      style={{
        ...styles.group,
        ...styles.group_effects,
        ...font?.base,
        ...styles.indents,
        backgroundImage: (hasGroupImage && `url(${imageUrl})`) || undefined,
        backgroundSize: getObjectFitDivMap(group.target.image?.objectFit),
        outline: isDropTarget ? '3px solid currentColor' : undefined,
      }}
    >
      <div className={s.mask} style={{ backgroundColor: group.target.overlay || undefined }} />
      <div className={labelClasses} style={{ padding: styles.indents.padding }}>
        {label}
      </div>
      {group.items.map((i) => {
        const isCorrect = R.last(R.values(testResult))

        return (
          <ScaledAnswer
            isCorrect={isCorrect as boolean}
            item={i}
            key={i.value}
            value={group.value}
          />
        )
      })}
    </div>
  )
}

export default DestinationCard
